import { Task, TaskMeta } from "@common/types/task";

export type MilestoneReviewMeta = NonNullable<TaskMeta["milestoneReview"]>;
export type MilestoneReviewApplication =
    MilestoneReviewMeta["applications"][number];
export type MilestoneReviewMilestone =
    MilestoneReviewApplication["dueMilestones"][number];
export type MilestoneReviewRow = MilestoneReviewMilestone &
    Pick<
        MilestoneReviewApplication,
        "applicationId" | "scheduleName" | "sourceLabel"
    > & { id: string };

export interface MilestoneReviewSummary {
    milestoneCount: number;
    scheduleCount: number;
    amount: number;
}

export function getMilestoneReviewApplications(
    task: Pick<Task, "metadata">,
): MilestoneReviewApplication[] {
    return task.metadata?.milestoneReview?.applications ?? [];
}

/**
 * Every due milestone of the task as one list, oldest expected date first,
 * minus the rows confirmed in this session.
 */
export function selectDueRows(options: {
    applications: readonly MilestoneReviewApplication[];
    confirmedPeriodIds: ReadonlySet<string>;
}): MilestoneReviewRow[] {
    return options.applications
        .flatMap((application) =>
            application.dueMilestones.map((milestone) => ({
                ...milestone,
                id: milestone.periodId,
                applicationId: application.applicationId,
                scheduleName: application.scheduleName,
                sourceLabel: application.sourceLabel,
            })),
        )
        .filter((row) => !options.confirmedPeriodIds.has(row.periodId))
        .sort(
            (left, right) =>
                left.expectedDate.localeCompare(right.expectedDate) ||
                left.sourceLabel.localeCompare(right.sourceLabel) ||
                left.sortOrder - right.sortOrder,
        );
}

export function summarizeDueRows(
    rows: readonly MilestoneReviewRow[],
): MilestoneReviewSummary {
    return {
        milestoneCount: rows.length,
        scheduleCount: new Set(rows.map((row) => row.applicationId)).size,
        amount: rows.reduce((sum, row) => sum + row.plannedAmount, 0),
    };
}

/** The source every row shares, or null when the rows span schedules. */
export function resolveSharedSourceLabel(
    rows: readonly MilestoneReviewRow[],
): string | null {
    const labels = new Set(rows.map((row) => row.sourceLabel));
    return labels.size === 1 ? rows[0].sourceLabel : null;
}
